/** The small "repeats" mark of a template that runs on a schedule (item 70) -
 *  in the list, on its dashboard card and on its page: "Repeats", or "Paused"
 *  while its schedule is paused, with the rule on hover and focus ("Every
 *  Wednesday at 18:00", the owner's zone, the next run). */
import { Tooltip } from 'antd';
import { PauseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import type { TFunc as T } from '@/utils/tfunc';
import type { InsightTemplate } from '../../types/insights';
import { instantLabel, weekOrder, weekdayName } from '../reports/reportParts';

type Scheduled = Pick<InsightTemplate, 'frequency' | 'weekdays' | 'month_day' | 'run_time'>;

/** "Every Wednesday at 18:00", "Every day at 06:00", "Every Sun, Mon at 09:00",
    "Day 1 of every month at 08:00" - or '' for no schedule. */
export function scheduleRule(t: T, template: Scheduled, language: string): string {
  const time = template.run_time;
  if (template.frequency === 'daily') {
    const days = template.weekdays || [];
    if (!days.length || days.length === 7) return t('insights.tpl.schedule.everyDay', { time });
    const names = weekOrder(language).filter((d) => days.includes(d)).map((d) => weekdayName(d)).join(', ');
    return t('insights.tpl.schedule.onDays', { days: names, time });
  }
  if (template.frequency === 'weekly') {
    return t('insights.tpl.schedule.everyWeek', { day: weekdayName(template.weekdays?.[0] ?? 0, 'long'), time });
  }
  if (template.frequency === 'monthly') {
    return t('insights.tpl.schedule.everyMonth', { day: template.month_day ?? 1, time });
  }
  return '';
}

export function ScheduleMark({ template, compact = false }: { template: InsightTemplate; compact?: boolean }) {
  const { t, language } = useLanguage();
  if (!template.frequency || template.frequency === 'none') return null;
  const rule = scheduleRule(t, template, language);
  const paused = template.schedule_paused;
  const next = template.next_runs?.[0]?.at;
  const tip = (
    <span className="tpl-tipline">
      <span>{paused ? t('insights.tpl.schedule.pausedRule', { rule }) : rule}</span>
      {template.zone && <span className="tpl-tipline__note">{t('insights.tpl.schedule.zone', { zone: template.zone })}</span>}
      {!paused && next && (
        <span className="tpl-tipline__note">{t('insights.tpl.schedule.next', { when: instantLabel(next, template.zone || undefined) })}</span>
      )}
    </span>
  );
  const name = paused ? t('insights.tpl.schedule.paused') : t('insights.tpl.schedule.repeats');
  return (
    <Tooltip title={tip} trigger={['hover', 'focus']}>
      <span className={`cx-pill tpl-repeats${paused ? ' is-paused' : ''}${compact ? ' tpl-repeats--compact' : ''}`}
            tabIndex={0} aria-label={`${name}: ${paused ? t('insights.tpl.schedule.pausedRule', { rule }) : rule}`}
            data-testid={`template-repeats-${template.template_id}`}>
        {paused ? <PauseCircleOutlined aria-hidden /> : <SyncOutlined aria-hidden />}
        {!compact && <span>{name}</span>}
      </span>
    </Tooltip>
  );
}
